import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import './components/login.css';
import loginwhite from './components/images/loginwhite.svg'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faComments, faStar, faEye, faEyeSlash } from "@fortawesome/free-regular-svg-icons";
import { faSearch, faExclamationCircle } from "@fortawesome/free-solid-svg-icons";

import { useNavigate, Link, useLocation } from "react-router-dom";
import { useLanguage } from "../../../i18n/LanguageContext";

function Login() {

  const { register, handleSubmit, setValue, formState: { errors } } = useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  const apiBaseUrl = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");

  const sessionExpired = new URLSearchParams(location.search).get("session") === "expired";
  const oauthError = new URLSearchParams(location.search).get("oauthError");

  const oauthErrorLabel = {
    google_auth_cancelled: "Google sign-in was cancelled.",
    google_auth_not_configured: "Google sign-in is not configured yet.",
    google_auth_email_provider_not_allowed: "This email provider is not allowed for registration.",
    google_auth_failed: "Google sign-in failed. Please try again.",
    google_auth_bad_state: "Google sign-in session expired. Please try again.",
    missing_token: "Google sign-in did not return a valid session.",
  };

  useEffect(()=>{
    const savedEmail = localStorage.getItem("rememberedEmail");
    if (savedEmail){ 
      setValue("email", savedEmail);
      setValue("rememberMe", true);
    }
  },[setValue]);

  const onSubmit = async (data) => {
    setLoginError("");
    setIsLoading(true);
    try {
      const response = await fetch("/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (response.ok) {
        if (data.rememberMe) {
          localStorage.setItem("rememberedEmail", data.email);
        } else {
          localStorage.removeItem("rememberedEmail");
        }

        localStorage.setItem("token", result.token);
        localStorage.setItem("role", result.role);
        localStorage.setItem("firstname", result.firstname || "");
        localStorage.setItem("lastname", result.lastname || "");
        localStorage.setItem("profileCompleted", "true");
        localStorage.setItem("authProvider", "password");
        localStorage.setItem("emailVerified", result.emailVerified ? "true" : "false");

        if(result.role === "owner"){
          navigate("/owner-dashboard");
        } 
        else if(result.role === "renter"){
          navigate("/renter-dashboard");
        } 
        else {
          navigate("/dashboard");
        }
      } 
      else {
        setIsLoading(false);
        setLoginError(result.message || "Login failed. Please check your credentials.");
      }
    } catch (error) {
      console.log(error);
      setIsLoading(false);
      setLoginError("Unable to connect to server. Please try again.");
    }
  };

  const startGoogleSignIn = () => {
    const targetUrl = apiBaseUrl ? `${apiBaseUrl}/api/auth/google` : "/api/auth/google";
    window.location.href = targetUrl;
  };


  return (
    <div className="auth-page container">
      <div className="d-flex flex-column auth-info-panel">
        <div className="p-2 d-flex justify-content-center">
          <img src="/logo.png" alt="GharBhada logo" className="auth-brand-logo" />
        </div>
        <div className="d-flex justify-content-center">
            <h1>{t('login.welcomeBack')}</h1>
        </div>
        <div className="text-muted text-center">
              <p>{t('login.subtitle')}</p>
        </div>
        <div className="d-flex mt-5 flex-column">
              <h3>{t('login.whyTitle')}</h3>
              <div className="d-flex flex-fill">
                <div className="p-2 features-svg">
                  <FontAwesomeIcon icon={faSearch} size="lg" className="text-primary" />
                </div>
                <div className="p-2 d-flex flex-column">
                  <p className="heading">{t('login.feature1Title')}</p>
                  <p className="description text-muted">{t('login.feature1Desc')}</p>
                </div>
              </div>
              <div className="d-flex flex-fill">
                <div className="p-2 features-svg">
                  <FontAwesomeIcon icon={faCheckCircle} size="lg" className="text-primary" />
                </div>
                <div className="p-2 d-flex flex-column">
                  <p className="heading">{t('login.feature2Title')}</p>
                  <p className="description text-muted">{t('login.feature2Desc')}</p>
                </div>
              </div>
              <div className="d-flex flex-fill">
                <div className="p-2 features-svg">
                  <FontAwesomeIcon icon={faComments} size="lg" className="text-primary" />
                </div>
                <div className="p-2 d-flex flex-column">
                  <p className="heading">{t('login.feature3Title')}</p>
                  <p className="description text-muted">{t('login.feature3Desc')}</p>
                </div>
              </div>
              <div className="d-flex flex-fill">
                <div className="p-2 features-svg">
                  <FontAwesomeIcon icon={faStar} size="lg" className="text-primary" />
                </div>
                <div className="p-2 d-flex flex-column">
                  <p className="heading">{t('login.feature4Title')}</p>
                  <p className="description text-muted">{t('login.feature4Desc')}</p>
                </div>
              </div>
        </div>
      </div>

      <div className="login d-flex flex-column auth-form-panel">
        <div className="d-flex justify-content-center">
          <img
            src="/icon.png"
            width="70"
            height="70"
            className="d-inline-block align-top mx-2"
            alt="GharBhada logo"
          />
        </div>
        <div className="d-flex justify-content-center">
          <h2>{t('login.welcomeBack')}</h2>
        </div>
        <div className="d-flex justify-content-center ">
          <p className="form-description text-muted">
            {t('login.subtitle')}
          </p>
        </div>

        {sessionExpired && (
          <div className="alert alert-warning d-flex align-items-start gap-2 py-2 px-3 mx-2 mb-1" role="alert" style={{ fontSize: ".875rem", borderRadius: 8 }}>
            <FontAwesomeIcon icon={faExclamationCircle} className="mt-1 flex-shrink-0" style={{ color: "#b45309" }} />
            <div>
              <strong style={{ display: "block" }}>{t("session.expiredTitle") || "Session Ended"}</strong>
              {t("session.expiredMsg") || "Your session has expired. Please sign in again to continue."}
            </div>
          </div>
        )}

        {oauthError && (
          <div className="alert alert-danger d-flex align-items-start gap-2 py-2 px-3 mx-2 mb-1" role="alert" style={{ fontSize: ".875rem", borderRadius: 8 }}>
            <FontAwesomeIcon icon={faExclamationCircle} className="mt-1 flex-shrink-0" />
            <div>{oauthErrorLabel[oauthError] || "Unable to complete social sign-in. Please try again."}</div>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <div className="form-group p-2">
            <label htmlFor="email">{t('login.email')}<span className="text-danger">*</span></label>
            <input type="email" className="form-control" {...register("email", {required:true, pattern:/^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/i,})} placeholder="Ex. johndoe@xyz.com"/>
            <span className="text-danger">
              {errors.email?.type==="required" && t('login.emailRequired')}
              {errors.email?.type==="pattern" && t('login.emailInvalid')}
            </span>
          </div>
          <div className="form-group p-2">
            <label htmlFor="password">{t('login.password')}<span className="text-danger">*</span></label>
            <div className="position-relative">
              <input 
                type={showPassword ? "text" : "password"} 
                className="form-control"
                {...register("password", {required:true, minLength:8})} 
                placeholder="Please enter a password"
              />
              <button
                type="button"
                className="btn btn-link position-absolute end-0 top-50 translate-middle-y"
                onClick={() => setShowPassword(!showPassword)}
                style={{ textDecoration: 'none', zIndex: 10 }}
              >
                <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} className="text-secondary" />
              </button>
            </div>
            <span className="text-danger">
              {errors.password?.type==="required" && t('login.passwordRequired')}
              {errors.email?.type==="minLength" && t('login.emailInvalid')}
            </span>
          </div>
          <div className="form-group p-2 auth-form-meta">
            <div className="form-check m-0">
              <input type="checkbox" className="form-check-input" {...register("rememberMe")}/>
              <label className="form-check-label px-2">{t('login.rememberMe')}</label>
            </div>
            <div>
              <Link to="/forgot-password" className="small text-decoration-none">{t('login.forgotPassword')}</Link>
            </div>
          </div>
          <div className="form-group p-2 d-flex">
            {loginError && (
              <div className="alert alert-danger py-2 px-3 w-100 mb-2 small">{loginError}</div>
            )}
          </div>
          <div className="form-group p-2 d-flex">
            <button type="submit" className="btn btn-primary flex-fill" disabled={isLoading}>
              {isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  {t('login.signingIn')}
                </>
              ) : (
                <>
                  <img src={loginwhite} width="30" height="30" className="d-inline-block align-top mx-2" alt="" />
                  {t('login.signIn')}
                </>
              )}
            </button>
          </div>
          <div className="form-group p-2 d-flex flex-column gap-2">
            <div className="auth-divider"><span>or</span></div>
            <button type="button" className="google-auth-btn" onClick={startGoogleSignIn}>
              <span className="google-auth-btn__icon">
                <svg width="20" height="20" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.717v2.258h2.908C16.658 14.013 17.64 11.706 17.64 9.2z" fill="#4285F4"/>
                  <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
                  <path d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.332z" fill="#FBBC05"/>
                  <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.96L3.964 7.293C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
                </svg>
              </span>
              <span className="google-auth-btn__text">Continue with Google</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Login;
